import { Box, LinearProgress } from '@mui/material';
import { createContext, useContext, useMemo, PropsWithChildren, ReactNode } from 'react';
import HelpIcon from '@mui/icons-material/Help';
import { GetMoreSearches } from 'src/common/config/TextMessage';
import { keywordResearchDashBoardCss, searchProgressSx } from 'src/content/keywordResearch/keywordResearchCSS';
import { CustomTooltip } from 'src/content/keywordResearch/CustomTooltip';

// 1. Define Context for state sharing between composed components
interface KAProgressContextProps {
  totalLimit: number;
  totalUsed: number;
  percentageUsed: number;
  progressBgColor: string;
  progressColor: string;
}

const KAProgressContext = createContext<KAProgressContextProps | undefined>(undefined);

export function useKAProgressContext() {
  const context = useContext(KAProgressContext);
  if (!context) {
    throw new Error('KAProgress subcomponents must be used within a <KAProgress /> component.');
  }
  return context;
}

// 2. Define Props Interfaces
export interface IKAProgressProps extends PropsWithChildren {
  totalLimit: number;
  totalUsed: number;
  // Kept for backward compatibility
  tooltip?: ReactNode;
}

export interface IKAProgressTextProps extends PropsWithChildren {
  className?: string;
}

export interface IKAProgressTooltipProps {
  title: ReactNode;
}

export interface IKAProgressActionProps extends PropsWithChildren {
  onClick?: () => void;
  className?: string;
}

export interface IKAProgressBarProps {
  className?: string;
}

// 3. Main/Root Component
function KAProgress(props: IKAProgressProps) {
  const { totalLimit, totalUsed, tooltip, children } = props;

  const percentageUsed = useMemo<number>(() => {
    if (totalLimit <= 0) return 0;
    return Math.min((totalUsed / totalLimit) * 100, 100);
  }, [totalLimit, totalUsed]);

  const getProgressBgColor = useMemo<string>(() => {
    if (percentageUsed < 60) {
      return 'var(--color-success-bg)'; // ✅ Light green
    } else if (percentageUsed < 100) {
      return 'rgba(255, 238, 210, 1)'; // 🟠 Light orange
    } else {
      return 'rgba(253, 232, 232, 1)'; // 🔴 Light red
    }
  }, [percentageUsed]);

  const getSearchProgressColor = useMemo<string>(() => {
    if (percentageUsed < 60) {
      return 'rgba(50, 213, 131, 0.8)'; // ✅ Green
    } else if (percentageUsed < 100) {
      return 'rgba(247, 144, 9, 1)'; // 🟠 Orange
    } else {
      return 'rgba(240, 68, 56, 1)'; // 🔴 Red
    }
  }, [percentageUsed]);

  const contextValue = useMemo(() => ({
    totalLimit,
    totalUsed,
    percentageUsed,
    progressBgColor: getProgressBgColor,
    progressColor: getSearchProgressColor
  }), [totalLimit, totalUsed, percentageUsed]);

  return (
    <KAProgressContext.Provider value={contextValue}>
      <div className="w-full">
        {children ? (
          children
        ) : (
          // Default/Legacy rendering mode if no children are provided
          <>
            <div className="mb-1 flex justify-between">
              <KAProgress.Text>
                {tooltip && <KAProgress.Tooltip title={tooltip} />}
              </KAProgress.Text>
              <KAProgress.Action />
            </div>
            <KAProgress.Bar />
          </>
        )}
      </div>
    </KAProgressContext.Provider>
  );
}

// 4. Subcomponents Implementation
KAProgress.Text = function KAProgressText({ children, className = '' }: IKAProgressTextProps) {
  const { totalUsed, totalLimit } = useKAProgressContext();
  return (
    <p className={`text-primary text-sm font-semibold ${className}`}>
      <b>{totalUsed}</b>{" "}of{" "}<b>{totalLimit}</b>{" "}searches used
      {children && (
        <span className="inline-flex items-center align-middle ml-1">
          {children}
        </span>
      )}
    </p>
  );
};

KAProgress.Tooltip = function KAProgressTooltip({ title }: IKAProgressTooltipProps) {
  return (
    <CustomTooltip title={<Box>{title}</Box>} placement="bottom" arrow>
      <HelpIcon sx={searchProgressSx?.helpIcon} />
    </CustomTooltip>
  );
};

KAProgress.Action = function KAProgressAction({ children, onClick, className = '' }: IKAProgressActionProps) {
  return (
    <div onClick={onClick} className={className}>
      <p className="font-bold text-sm text-[var(--color-success-dark)] underline underline-offset-2 cursor-pointer">
        {children || GetMoreSearches}
      </p>
    </div>
  );
};

KAProgress.Bar = function KAProgressBar({ className = '' }: IKAProgressBarProps) {
  const { percentageUsed, progressBgColor, progressColor } = useKAProgressContext();
  return (
    <LinearProgress
      value={percentageUsed}
      variant="determinate"
      className={`w-full ${className}`}
      sx={{
        borderRadius: '20px',
        height: '6px',
        bgcolor: progressBgColor, // dynamic background
        '& .MuiLinearProgress-bar': {
          backgroundColor: progressColor
        }
      }}
    />
  );
};

export default KAProgress;
